/**
 * Инициализация моста web ↔ native и системные подписки, не требующие роутера
 * (init / locale / theme). Back-навигация и canGoBack живут в
 * `WebViewBridgeProvider`, т.к. им нужен доступ к react-router.
 *
 * Всё под гейтом `isInWebView`: в обычном браузере `setupBridge()` — no-op,
 * текущее поведение не меняется.
 */
import { apiConnector } from '@/api/api.connector';
import i18next from '@/i18n';
import { Languages, SUPPORTED_LANGUAGES } from '@/shared/constants';
import { dispatch } from '@/store';
import {
  setIsInWebView,
  setTheme,
  setWebviewProperties
} from '@/store/app.slice';

import { safeLocalStorage } from '../services/helpers/DOM';
import { getAppNavigate } from '../services/helpers/navigate-helper';
import { type InitContext, SYSTEM_EVENTS, bridge } from './vendor/web';

/** Класс на <html>, под который вешается webview-специфичный CSS (safe-area). */
export const WEBVIEW_ROOT_CLASS = 'is-webview';

/**
 * Нормализуем локаль от нативы к поддерживаемым приложением кодам.
 * Натива может прислать ISO-код ('kk', 'ru-RU', 'en-US') — берём базовый
 * сегмент и маппим казахский 'kk' → 'kz' (внутренний код приложения).
 */
const normalizeLocale = (locale: string): Languages | null => {
  const base = locale.toLowerCase().split(/[-_]/)[0];
  const mapped = base === 'kk' ? Languages.kk : (base as Languages);
  return SUPPORTED_LANGUAGES.includes(mapped) ? mapped : null;
};

/**
 * Сменить язык приложения БЕЗ перехода по URL (WebView не перезагружается,
 * адресная строка с локалью остаётся прежней — на неё завязаны QR-коды).
 */
const applyLocale = (locale: string): void => {
  const lang = normalizeLocale(locale);
  if (lang && i18next.language !== lang) i18next.changeLanguage(lang);
};

/**
 * Применяем тему от нативы: вешаем `data-theme` на <html> (под него завязана
 * CSS-переменная тёмной палитры в styles/theme.scss) и кладём в стор —
 * redux-`theme` отдельно питает antd ConfigProvider.
 */
const applyTheme = (mode: 'light' | 'dark'): void => {
  document.documentElement.setAttribute('data-theme', mode);
  dispatch(setTheme(mode));
  safeLocalStorage.set('native-theme', mode);
};

/** Прокидываем safe-area insets от нативы в CSS-переменные. */
const applyInsets = (insets: InitContext['insets']): void => {
  const root = document.documentElement.style;
  root.setProperty('--sai-top', `${insets.top}px`);
  root.setProperty('--sai-right', `${insets.right}px`);
  root.setProperty('--sai-bottom', `${insets.bottom}px`);
  root.setProperty('--sai-left', `${insets.left}px`);
};

/** Добавляем viewport-fit=cover, чтобы env(safe-area-inset-*) заработал под вырезами. */
const ensureViewportFitCover = (): void => {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!meta || /viewport-fit/.test(meta.content)) return;
  meta.content = `${meta.content}, viewport-fit=cover, user-scalable=no, maximum-scale=1.0, minimum-scale=1.0`;
};

const clearInsets = (): void => {
  const root = document.documentElement.style;
  root.removeProperty('--sai-top');
  root.removeProperty('--sai-right');
  root.removeProperty('--sai-bottom');
  root.removeProperty('--sai-left');
};

const navigateToInitialPath = async (initialPath?: string) => {
  if (!initialPath) return;
  const isSsoAuthorized = await apiConnector.ssoAuthorize();
  if (!isSsoAuthorized) return;
  const navigate = getAppNavigate();
  navigate(initialPath, { replace: true });
};

/**
 * Подключить мост и системные подписки. Вызывается один раз при монтировании
 * клиентского модуля.
 *
 * @returns teardown — снять подписки и освободить мост (вызвать при размонтировании).
 */
export function setupBridge(onFinishSetup?: () => void) {
  // init — первое событие после bridge.ready. Несёт контекст приложения.
  // Буферизуется нативной стороной, поэтому подписка до bridge.init() ничего не теряет.
  // Деградации в браузерный режим нет: если натива не пришлёт init, приложение
  // остаётся в ожидании, но веб-версией для нативки не подменяется.
  const offInit = bridge.on<InitContext>(SYSTEM_EVENTS.Init, async (ctx) => {
    // Webview-косметику применяем после подтверждённого handshake.
    document.documentElement.classList.add(WEBVIEW_ROOT_CLASS);
    ensureViewportFitCover();
    applyLocale(ctx.locale);
    applyInsets(ctx.insets);
    dispatch(setIsInWebView(true));
    applyTheme(ctx.mode);
    // Свойства от нативки (город и пр.) кладём в стор «как есть», без запросов.
    // Резолв cityId → city живёт в useApiRequests, когда города подгрузятся.
    dispatch(setWebviewProperties(ctx.webProperties ?? {}));

    await navigateToInitialPath(ctx.webProperties?.initialPath);
    onFinishSetup?.();
  });

  const offTheme = bridge.on<'light' | 'dark'>(
    SYSTEM_EVENTS.ThemeChanged,
    (mode) => applyTheme(mode)
  );

  // Смена языка из приложения — переключаем i18next без смены URL.
  const offLocale = bridge.on<{ locale: string }>(
    SYSTEM_EVENTS.LocaleChanged,
    ({ locale }) => applyLocale(locale)
  );

  bridge.init({
    targetOrigin: process.env.PUBLIC_PWA_URL,
    allowedOrigins: [
      process.env.PUBLIC_PWA_URL,
      process.env.PUBLIC_ALLUR_AUTO_CREDIT_URL
    ]
  }); // подключается к __nativeBridge и шлёт bridge.ready

  return () => {
    offInit();
    offTheme();
    offLocale();
    bridge.destroy();
    document.documentElement.classList.remove(WEBVIEW_ROOT_CLASS);
    // Возвращаем светлый базис как в разметке index.html, а не снимаем атрибут:
    // снятый `data-theme` уводил CSS-переменные в светлую тему, тогда как
    // redux-`theme` оставался тёмным — antd рисовал тёмные поповеры на светлом.
    document.documentElement.setAttribute('data-theme', 'light');
    dispatch(setTheme('light'));
    clearInsets();
    dispatch(setIsInWebView(false));
  };
}
